import React from "react";
import { formatDateTime, getActionBadgeClass, formatLocation } from "./utils";

const renderDeviceInfo = (deviceInfo) => {
  if (!deviceInfo) return "-";
  const os = String(deviceInfo.os || "").toLowerCase();
  const browser = String(deviceInfo.browser || "").toLowerCase();
  const device = String(deviceInfo.device || "").toLowerCase();

  let osIcon = <i className="bi bi-cpu text-muted me-1" title={deviceInfo.os}></i>;
  if (os.includes("win")) osIcon = <i className="bi bi-windows text-primary me-1" title={deviceInfo.os}></i>;
  else if (os.includes("mac") || os.includes("osx") || os.includes("ios")) osIcon = <i className="bi bi-apple text-dark me-1" title={deviceInfo.os}></i>;
  else if (os.includes("android")) osIcon = <i className="bi bi-android text-success me-1" title={deviceInfo.os}></i>;
  else if (os.includes("linux") || os.includes("ubuntu")) osIcon = <i className="bi bi-ubuntu text-danger me-1" title={deviceInfo.os}></i>;

  let browserIcon = <i className="bi bi-globe text-muted me-1" title={deviceInfo.browser}></i>;
  if (browser.includes("chrome")) browserIcon = <i className="bi bi-browser-chrome text-primary me-1" title={deviceInfo.browser}></i>;
  else if (browser.includes("firefox")) browserIcon = <i className="bi bi-browser-firefox text-warning me-1" title={deviceInfo.browser}></i>;
  else if (browser.includes("safari")) browserIcon = <i className="bi bi-browser-safari text-info me-1" title={deviceInfo.browser}></i>;
  else if (browser.includes("edge")) browserIcon = <i className="bi bi-browser-edge text-primary me-1" title={deviceInfo.browser}></i>;

  let deviceIcon = <i className="bi bi-display text-muted me-1" title={deviceInfo.device}></i>;
  if (device.includes("desktop") || device.includes("laptop")) deviceIcon = <i className="bi bi-laptop text-dark me-1" title={deviceInfo.device}></i>;
  else if (device.includes("mobile") || device.includes("phone")) deviceIcon = <i className="bi bi-phone text-dark me-1" title={deviceInfo.device}></i>;
  else if (device.includes("tablet")) deviceIcon = <i className="bi bi-tablet text-dark me-1" title={deviceInfo.device}></i>;

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <span className="d-inline-flex align-items-center text-nowrap" title={`OS: ${deviceInfo.os}`}>
        {osIcon} <span className="small text-muted">{deviceInfo.os || "OS"}</span>
      </span>
      <span className="d-inline-flex align-items-center text-nowrap" title={`Browser: ${deviceInfo.browser}`}>
        {browserIcon} <span className="small text-muted">{deviceInfo.browser || "Browser"}</span>
      </span>
      <span className="d-inline-flex align-items-center text-nowrap" title={`Device: ${deviceInfo.device}`}>
        {deviceIcon} <span className="small text-muted">{deviceInfo.device || "Device"}</span>
      </span>
    </div>
  );
};

const renderMetaValue = (key, val) => {
  if (val === null || val === undefined) return "-";
  if (typeof val === "object") return JSON.stringify(val);
  
  const lowerKey = key.toLowerCase();
  if (lowerKey.includes("amount") || lowerKey.includes("price") || lowerKey.includes("charge") || lowerKey.includes("fee")) {
    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
      }).format(val);
    } catch (e) {
      return `₹${val}`;
    }
  }
  return String(val);
};

const formatKeyLabel = (key) => {
  const result = key
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .trim();
  return result.charAt(0).toUpperCase() + result.slice(1);
};

function LogDetails({ selectedLog, onClose, onCopy, copied }) {
  if (!selectedLog) return null;

  return (
    <div className="card shadow-sm border-0 sticky-top" style={{ top: "80px", zIndex: 10 }}>
      <div className="card-header bg-light border-bottom d-flex justify-content-between align-items-center py-3">
        <h6 className="card-title fw-bold mb-0 text-primary">
          <i className="bi bi-info-circle-fill me-2"></i>Audit Log Details
        </h6>
        <div className="d-flex align-items-center gap-2">
          {onCopy && (
            <button
              type="button"
              className={`btn btn-xs ${copied ? "btn-success" : "btn-outline-secondary"}`}
              onClick={onCopy}
              title="Copy Log JSON"
            >
              {copied ? (
                <>
                  <i className="bi bi-check-lg me-1"></i>Copied
                </>
              ) : (
                <>
                  <i className="bi bi-clipboard me-1"></i>Copy JSON
                </>
              )}
            </button>
          )}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
          ></button>
        </div>
      </div>
      <div className="card-body overflow-auto" style={{ maxHeight: "calc(100vh - 220px)" }}>
        
        {/* Meta details header card */}
        <div className="p-3 bg-light rounded-3 mb-3 border">
          <div className="mb-2">
            <span className="small text-muted d-block">Action performed</span>
            <span className={`badge fs-6 ${getActionBadgeClass(selectedLog.action)}`}>
              {selectedLog.action}
            </span>
          </div>
          <div className="row g-2">
            <div className="col-6">
              <span className="small text-muted d-block">User ID</span>
              <span className="fw-semibold">{selectedLog.userId || "-"}</span>
            </div>
            <div className="col-6">
              <span className="small text-muted d-block">Booking ID</span>
              <span className="fw-semibold text-primary">
                {selectedLog.meta?.bookingId || selectedLog.bookingId || "-"}
              </span>
            </div>
          </div>
        </div>

        {/* Request Information */}
        <h6 className="fw-bold border-bottom pb-2 mb-2 text-secondary small uppercase tracking-wider">
          Request Information
        </h6>
        <div className="mb-3">
          <div className="mb-2">
            <span className="small text-muted d-block">Path API</span>
            <div className="p-2 bg-light border rounded font-monospace small text-break">
              {selectedLog.path || "-"}
            </div>
          </div>
          <div className="row g-2">
            <div className="col-6">
              <span className="small text-muted d-block">IP Address</span>
              <span className="font-monospace small">{selectedLog.ip || "-"}</span>
            </div>
            <div className="col-6">
              <span className="small text-muted d-block">Timestamp</span>
              <span className="small">{formatDateTime(selectedLog.createdAt)}</span>
            </div>
          </div>
        </div>

        {/* Location Card */}
        {selectedLog.location && (
          <>
            <h6 className="fw-bold border-bottom pb-2 mb-2 text-secondary small uppercase tracking-wider">
              Location
            </h6>
            <div className="mb-3 d-flex align-items-start gap-2">
              <i className="bi bi-geo-alt-fill text-danger fs-5 mt-1"></i>
              <div>
                <span className="fw-semibold d-block">
                  {formatLocation(selectedLog.location)}
                </span>
                <span className="small text-muted">
                  City: {selectedLog.location.city || "-"} | Region: {selectedLog.location.region || "-"}
                </span>
              </div>
            </div>
          </>
        )}

        {/* Device & Client Info */}
        {selectedLog.deviceInfo && (
          <>
            <h6 className="fw-bold border-bottom pb-2 mb-2 text-secondary small uppercase tracking-wider">
              Client / Device Details
            </h6>
            <div className="mb-3 p-2 border rounded bg-white">
              {renderDeviceInfo(selectedLog.deviceInfo)}
            </div>
          </>
        )}

        {/* Meta details mapping */}
        {selectedLog.meta && Object.keys(selectedLog.meta).length > 0 && (
          <>
            <h6 className="fw-bold border-bottom pb-2 mb-2 text-secondary small uppercase tracking-wider">
              Action Parameters / Meta
            </h6>
            <div className="table-responsive mb-3 border rounded">
              <table className="table table-sm table-striped table-hover mb-0 small">
                <tbody>
                  {Object.entries(selectedLog.meta).map(([key, val]) => (
                    <tr key={key}>
                      <td className="text-muted fw-semibold" style={{ width: "45%" }}>
                        {formatKeyLabel(key)}
                      </td>
                      <td className="text-break font-monospace">
                        {renderMetaValue(key, val)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LogDetails;
